import {
  IconName,
  PositionAssetTypeEnum,
  PositionUpdateSourceEnum,
  isTalosUser,
  removeEmptyFilters,
  useSecuritiesContext,
  useSymbolsFilter,
  type FilterClause,
  type FilterableProperty,
  type Security,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isEqual, omit, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useUser } from '../../../../hooks';
import { useMarketAccountsFilter } from '../../../../hooks/filters/useMarketAccountsFilter';
import { usePredictionEventsFilter } from '../../../../hooks/filters/usePredictionEventsFilter';
import { usePredictionSeriesFilter } from '../../../../hooks/filters/usePredictionSeriesFilter';
import type { PositionsTableFilter } from '../types';
import { usePositionsBaseFilter, type UsePositionsBaseFilterParams } from '../usePositionsBaseFilter';

interface UsePositionsPredictionsFilterParams extends UsePositionsBaseFilterParams {
  defaultFilter: PositionsTableFilter;
}

export const PREDICTIONS_PRODUCT_TYPES = [PositionAssetTypeEnum.PredictionOutcome];

export function toPredictionsServerFilter(filter: PositionsTableFilter | undefined): PositionsTableFilter {
  return {
    ...omit(filter, ['Series', 'Events']),
    AssetTypes: PREDICTIONS_PRODUCT_TYPES,
  };
}

function toPositionUpdateSource(value: string | undefined): PositionUpdateSourceEnum | undefined {
  if (value === PositionUpdateSourceEnum.Gateway || value === PositionUpdateSourceEnum.Internal) {
    return value;
  }
  return undefined;
}

export function listPredictionOutcomesForSymbolFilter(predictionOutcomesBySymbol: Map<string, Security>): Security[] {
  return [...predictionOutcomesBySymbol.values()];
}

export const usePositionsPredictionsFilter = ({ defaultFilter, ...params }: UsePositionsPredictionsFilterParams) => {
  const user = useUser();
  const { predictionOutcomesBySymbol } = useSecuritiesContext();
  const predictionOutcomes = useMemo(
    () => listPredictionOutcomesForSymbolFilter(predictionOutcomesBySymbol),
    [predictionOutcomesBySymbol]
  );

  const baseFilter = usePositionsBaseFilter(params);
  const symbolsFilter = useSymbolsFilter(predictionOutcomes);
  const accountsFilter = useMarketAccountsFilter();
  const seriesFilter = usePredictionSeriesFilter();
  const eventsFilter = usePredictionEventsFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof PositionsTableFilter>>([
        seriesFilter,
        eventsFilter,
        { ...symbolsFilter, key: 'Symbols' },
        accountsFilter,
        isTalosUser(user) && {
          key: 'Source',
          label: 'Source',
          icon: IconName.CubeTransparent,
          control: 'single-select',
          options: Object.keys(PositionUpdateSourceEnum),
          getOptionLabel: (option: string) => startCase(option),
        },
      ]),
    [seriesFilter, eventsFilter, symbolsFilter, accountsFilter, user]
  );

  const { changeFilter, initialFilterClauses } = baseFilter;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          ...Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ),
          Source: isTalosUser(user)
            ? toPositionUpdateSource(filterClausesByPropertyKey.get('Source')?.selections?.[0])
            : PositionUpdateSourceEnum.Gateway,
          AssetTypes: defaultFilter.AssetTypes,
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, defaultFilter.AssetTypes, user]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    filterBuilderProps,
    ...baseFilter,
  };
};
